<template>
  <aside class="rightbar" data-tutorial="layers">
    <div class="header">
      <div class="title">Layers</div>
      <div class="meta">{{ totalCount }} item<span v-if="totalCount !== 1">s</span></div>
    </div>

    <div class="groups">
      <section class="group" v-for="group in grouped" :key="group.key">
        <div class="group-head">
          <button class="group-title" type="button" @click="toggle(group.key)">
            <span>{{ group.label }}</span>
            <span class="count">{{ group.items.length }}</span>
          </button>

          <button class="danger" type="button" title="Delete all in group" @click="deleteGroup(group.key)">
            Delete all
          </button>
        </div>

        <ul v-if="expanded[group.key]" class="items">
          <li v-for="f in group.items" :key="featureId(f)" class="item" :class="{ selected: isSelected(f), editing: editing && editingId === featureId(f), duplicate: isDuplicatePoint(f) }" :data-feature-id="featureId(f)">
            <div class="item-row">
              <button class="item-main" type="button" @click="selectAndZoom(f)">
                <span v-if="isPointFeature(f)" class="poi-icon-wrap">
                  <img v-if="f.properties?.iconUrl" :src="f.properties.iconUrl" alt="" class="poi-icon" />
                  <span v-else class="poi-fallback">{{ f.properties?.iconSymbol || '•' }}</span>
                </span>
                <span v-else class="dot" :style="{ background: safeColor(f.properties?.color) }"></span>
                <div class="item-text">
                  <div class="item-name">{{ displayName(f) }}</div>
                  <div class="item-sub">{{ featureSubtitle(f) }}</div>
                  <div v-if="isDuplicatePoint(f)" class="duplicate-warning" role="status">Duplicate coordinates</div>
                </div>
              </button>

              <div class="actions">
                <button class="icon" type="button" title="Edit" :aria-label="`Edit ${displayName(f)}`" @click="startEdit(f)">✎</button>
                <button class="icon danger" type="button" title="Delete" :aria-label="`Delete ${displayName(f)}`" @click="del(f)">✕</button>
              </div>
            </div>

            <div v-if="editing && editingId === featureId(f)" class="editor">
              <div class="editor-title">Edit feature</div>

              <div class="field">
                <label for="edit-name">Name</label>
                <input id="edit-name" ref="editorNameRef" v-model="editForm.name" type="text" />
              </div>

              <div class="field">
                <label for="edit-description">Description</label>
                <input id="edit-description" v-model="editForm.description" type="text" />
              </div>

              <div class="field">
                <label for="edit-color-text">Colour</label>
                <div class="color-row">
                  <input v-model="editForm.color" type="color" class="color-picker" aria-label="Pick feature colour" />
                  <input id="edit-color-text" v-model="editForm.color" type="text" placeholder="#RRGGBB" />
                </div>
              </div>

              <div v-if="isEditingPoint" class="field">
                <label>Icon</label>
                <IconPicker v-model="editForm.iconUrl" :icons="kmlIcons" />
              </div>

              <template v-if="isEditingCircle || isEditingSector">
                <div class="field">
                  <label for="edit-center">Center coordinate (any supported format)</label>
                  <input id="edit-center" v-model="editForm.centerText" type="text" placeholder="-32.9283, 151.7817" />
                </div>

                <div class="grid2" :class="{ 'wide-grid': isEditingSector }">
                  <div class="field">
                    <label for="edit-radius">Radius</label>
                    <input id="edit-radius" v-model.number="editForm.radius" type="number" min="0" step="0.1" />
                  </div>
                  <div class="field">
                    <label for="edit-unit">Unit</label>
                    <select id="edit-unit" v-model="editForm.radiusUnit">
                      <option v-for="unit in circleUnits" :key="unit.value" :value="unit.value">{{ unit.label }}</option>
                    </select>
                  </div>
                  <div v-if="isEditingSector" class="field">
                    <label for="edit-direction">Direction</label>
                    <select id="edit-direction" v-model="editForm.arcDirection">
                      <option v-for="direction in arcDirections" :key="direction.value" :value="direction.value">{{ direction.label }}</option>
                    </select>
                  </div>
                </div>

                <div v-if="isEditingSector" class="grid2 wide-grid">
                  <div class="field">
                    <label for="edit-start-bearing">Start bearing</label>
                    <input id="edit-start-bearing" v-model.number="editForm.startBearing" type="number" step="1" />
                  </div>
                  <div class="field">
                    <label for="edit-end-bearing">End bearing</label>
                    <input id="edit-end-bearing" v-model.number="editForm.endBearing" type="number" step="1" />
                  </div>
                </div>

                <div class="field">
                  <label for="edit-opacity">Opacity</label>
                  <input id="edit-opacity" v-model.number="editForm.opacity" type="number" min="0" max="1" step="0.05" />
                </div>
              </template>

              <template v-else-if="isEditingArc">
                <div class="field">
                  <label for="edit-arc-center">Center coordinate (any supported format)</label>
                  <input id="edit-arc-center" v-model="editForm.centerText" type="text" placeholder="-32.9283, 151.7817" />
                </div>

                <div class="grid2 wide-grid">
                  <div class="field">
                    <label for="edit-arc-radius">Radius</label>
                    <input id="edit-arc-radius" v-model.number="editForm.radius" type="number" min="0" step="0.1" />
                  </div>
                  <div class="field">
                    <label for="edit-arc-unit">Unit</label>
                    <select id="edit-arc-unit" v-model="editForm.radiusUnit">
                      <option v-for="unit in circleUnits" :key="unit.value" :value="unit.value">{{ unit.label }}</option>
                    </select>
                  </div>
                  <div class="field">
                    <label for="edit-arc-direction">Direction</label>
                    <select id="edit-arc-direction" v-model="editForm.arcDirection">
                      <option v-for="direction in arcDirections" :key="direction.value" :value="direction.value">{{ direction.label }}</option>
                    </select>
                  </div>
                </div>

                <div class="grid2 wide-grid">
                  <div class="field">
                    <label for="edit-arc-start-bearing">Start bearing</label>
                    <input id="edit-arc-start-bearing" v-model.number="editForm.startBearing" type="number" step="1" />
                  </div>
                  <div class="field">
                    <label for="edit-arc-end-bearing">End bearing</label>
                    <input id="edit-arc-end-bearing" v-model.number="editForm.endBearing" type="number" step="1" />
                  </div>
                </div>
              </template>

              <div v-else-if="isEditingPolygon" class="field">
                <label for="edit-poly-opacity">Opacity</label>
                <input id="edit-poly-opacity" v-model.number="editForm.opacity" type="number" min="0" max="1" step="0.05" />
              </div>

              <div v-if="isEditingPolygon && !isEditingCircle && !isEditingSector" class="field">
                <label for="edit-vertices">Vertices (lat, lng — one per line)</label>
                <textarea id="edit-vertices" v-model="editForm.verticesText" class="coords-input" placeholder="-32.9283, 151.7817"></textarea>
              </div>

              <p v-if="editError" class="error">{{ editError }}</p>

              <div class="row">
                <button class="primary" type="button" @click="saveEdit">Save</button>
                <button class="secondary" type="button" @click="cancelEdit">Cancel</button>
                <button class="danger" type="button" @click="deleteEditing">Delete</button>
              </div>
            </div>
          </li>
        </ul>
      </section>

      <div v-if="totalCount === 0" class="empty">No features yet. Add points/paths/polygons from the left.</div>
    </div>
  </aside>
</template>

<script setup>
import { computed, nextTick, reactive, ref, watch } from "vue"
import { parseCoordinateLine, parseCoordinateListDetailed } from "@/utils/coordinates"
import { formatCoordinatePair } from "@/utils/coordinateFormat"
import { findDuplicatePointIds } from "@/utils/dedup"
import { ARC_DIRECTIONS, buildArcGeometry, buildCircleGeometry, buildSectorGeometry, CIRCLE_UNITS } from "@/utils/circles"
import { GOOGLE_KML_ICONS } from "@/utils/kmlIcons"
import IconPicker from "@/components/IconPicker.vue"

const props = defineProps({
  featureCollection: { type: Object, required: true },
  coordinateFormat: { type: String, default: "ddm" },
})

const emit = defineEmits([
  "zoom-to-feature",
  "delete-feature",
  "delete-group",
  "update-feature"
])

const expanded = reactive({ points: true, lines: true, polygons: true, other: false })

function toggle(key) {
  expanded[key] = !expanded[key]
}

const features = computed(() => props.featureCollection?.features || [])
const totalCount = computed(() => features.value.length)

const grouped = computed(() => {
  const pts = []
  const lines = []
  const polys = []
  const other = []

  for (const f of features.value) {
    const t = f?.geometry?.type
    if (t === "Point" || t === "MultiPoint") pts.push(f)
    else if (t === "LineString" || t === "MultiLineString") lines.push(f)
    else if (t === "Polygon" || t === "MultiPolygon") polys.push(f)
    else other.push(f)
  }

  const byName = (a, b) => displayName(a).localeCompare(displayName(b))

  pts.sort(byName)
  lines.sort(byName)
  polys.sort(byName)
  other.sort(byName)

  const groups = [
    { key: "points", label: "Points of Interest", items: pts },
    { key: "lines", label: "Paths", items: lines },
    { key: "polygons", label: "Polygons", items: polys },
    { key: "other", label: "Other", items: other }
  ]

  return groups.filter((g) => g.items.length > 0)
})

function featureId(f) {
  return String(f?.id ?? f?.properties?.id ?? "")
}

const duplicatePointIds = computed(() => findDuplicatePointIds(props.featureCollection))

function isDuplicatePoint(f) {
  return duplicatePointIds.value.has(featureId(f))
}

function displayName(f) {
  return f?.properties?.name || "Unnamed"
}

function formatFeatureNumber(value, digits = 1) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return ""
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(digits).replace(/\.0+$/, "")
}

function featureSubtitle(f) {
  if (f?.properties?.featureType === "circle") {
    const radius = formatFeatureNumber(f?.properties?.radius)
    const unit = String(f?.properties?.radiusUnit || "nm").toUpperCase()
    return radius ? `Circle • ${radius} ${unit} radius` : "Circle"
  }
  if (f?.properties?.featureType === "sector") {
    const radius = formatFeatureNumber(f?.properties?.radius)
    const unit = String(f?.properties?.radiusUnit || "nm").toUpperCase()
    const sweep = formatFeatureNumber(f?.properties?.arcSweepDegrees, 0)
    const direction = String(f?.properties?.arcDirection || "").trim().toUpperCase()
    if (radius && sweep && direction) return `Sector • ${radius} ${unit} • ${direction} ${sweep}°`
    if (radius && sweep) return `Sector • ${radius} ${unit} • ${sweep}°`
    return "Sector"
  }
  if (f?.properties?.featureType === "arc") {
    const radius = formatFeatureNumber(f?.properties?.radius)
    const unit = String(f?.properties?.radiusUnit || "nm").toUpperCase()
    const sweep = formatFeatureNumber(f?.properties?.arcSweepDegrees, 0)
    const direction = String(f?.properties?.arcDirection || "").trim().toUpperCase()
    if (radius && sweep && direction) return `Arc • ${radius} ${unit} • ${direction} ${sweep}°`
    if (radius && sweep) return `Arc • ${radius} ${unit} • ${sweep}°`
    return "Arc"
  }
  return f?.geometry?.type || "Feature"
}

function safeColor(input) {
  const c = String(input || "").trim()
  return /^#([0-9a-fA-F]{6})$/.test(c) ? c : "#000000"
}

function isPointFeature(f) {
  const t = f?.geometry?.type
  return t === "Point" || t === "MultiPoint"
}

const selectedId = ref(null)
function isSelected(f) {
  return selectedId.value && selectedId.value === featureId(f)
}
function selectAndZoom(f) {
  selectedId.value = featureId(f)
  emit("zoom-to-feature", f)
}

function del(f) {
  emit("delete-feature", f)
  if (isSelected(f)) selectedId.value = null
  if (editing.value && editingId.value === featureId(f)) cancelEdit()
}

function deleteGroup(groupKey) {
  emit("delete-group", groupKey)
  if (editing.value) cancelEdit()
}

const editing = ref(false)
const editingId = ref(null)
const editorNameRef = ref(null)
const editingGeometryType = ref("")
const editFeatureType = ref("")
const editError = ref("")
const editForm = reactive({
  name: "",
  description: "",
  color: "",
  iconUrl: "",
  opacity: 0.25,
  verticesText: "",
  centerText: "",
  radius: "",
  radiusUnit: "nm",
  startBearing: 0,
  endBearing: 90,
  arcDirection: "cw",
})
const kmlIcons = GOOGLE_KML_ICONS
const circleUnits = CIRCLE_UNITS
const arcDirections = ARC_DIRECTIONS

const isEditingPoint = computed(() => editingGeometryType.value === "Point")
const isEditingPolygon = computed(() => editingGeometryType.value === "Polygon")
const isEditingCircle = computed(() => isEditingPolygon.value && editFeatureType.value === "circle")
const isEditingSector = computed(() => isEditingPolygon.value && editFeatureType.value === "sector")
const isEditingArc = computed(() => editingGeometryType.value === "LineString" && editFeatureType.value === "arc")

function coordinateToText(lat, lng) {
  return formatCoordinatePair(lat, lng, props.coordinateFormat)
}

function polygonToText(feature) {
  const ring = feature?.geometry?.coordinates?.[0] || []
  return ring.slice(0, -1).map(([lng, lat]) => coordinateToText(lat, lng)).join("\n")
}

function circleCenterText(feature) {
  const lat = Number(feature?.properties?.centerLat)
  const lng = Number(feature?.properties?.centerLng)
  if (Number.isFinite(lat) && Number.isFinite(lng)) return coordinateToText(lat, lng)
  const first = feature?.geometry?.coordinates?.[0]?.[0]
  if (!Array.isArray(first) || first.length < 2) return ""
  return coordinateToText(first[1], first[0])
}

function groupKeyForFeature(f) {
  const type = f?.geometry?.type
  if (type === "Point" || type === "MultiPoint") return "points"
  if (type === "LineString" || type === "MultiLineString") return "lines"
  if (type === "Polygon" || type === "MultiPolygon") return "polygons"
  return "other"
}

function startEdit(f) {
  expanded[groupKeyForFeature(f)] = true
  selectedId.value = featureId(f)
  editing.value = true
  editingId.value = featureId(f)
  editingGeometryType.value = f?.geometry?.type || ""
  editFeatureType.value = String(f?.properties?.featureType || "")
  editError.value = ""

  editForm.name = f?.properties?.name || ""
  editForm.description = f?.properties?.description || ""
  editForm.color = f?.properties?.color || ""
  editForm.iconUrl = f?.properties?.iconUrl || GOOGLE_KML_ICONS[0].url
  editForm.opacity = Number(f?.properties?.opacity ?? 0.25)
  editForm.verticesText = editingGeometryType.value === "Polygon" && editFeatureType.value !== "circle" && editFeatureType.value !== "sector" ? polygonToText(f) : ""
  editForm.centerText = editFeatureType.value === "circle" || editFeatureType.value === "arc" || editFeatureType.value === "sector" ? circleCenterText(f) : ""
  editForm.radius = Number(f?.properties?.radius ?? "") || ""
  editForm.radiusUnit = String(f?.properties?.radiusUnit || "nm")
  editForm.startBearing = Number(f?.properties?.startBearing ?? 0)
  editForm.endBearing = Number(f?.properties?.endBearing ?? 90)
  editForm.arcDirection = String(f?.properties?.arcDirection || "cw")

  nextTick(() => {
    const input = Array.isArray(editorNameRef.value) ? editorNameRef.value[0] : editorNameRef.value
    input?.focus()
    input?.closest(".editor")?.scrollIntoView({ block: "nearest" })
  })
}

function cancelEdit() {
  editing.value = false
  editingId.value = null
  editingGeometryType.value = ""
  editFeatureType.value = ""
  editError.value = ""
  editForm.name = ""
  editForm.description = ""
  editForm.color = ""
  editForm.iconUrl = ""
  editForm.opacity = 0.25
  editForm.verticesText = ""
  editForm.centerText = ""
  editForm.radius = ""
  editForm.radiusUnit = "nm"
  editForm.startBearing = 0
  editForm.endBearing = 90
  editForm.arcDirection = "cw"
}

function parseVerticesText(text) {
  const parsed = parseCoordinateListDetailed(text)
  if (parsed.error) return { error: parsed.error }
  if (parsed.coords.length < 3) return { error: "Polygon needs at least 3 vertices." }
  const ring = [...parsed.coords]
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first)
  return { geometry: { type: "Polygon", coordinates: [ring] } }
}

function saveEdit() {
  editError.value = ""
  const id = editingId.value
  if (!id) return

  const name = String(editForm.name || "").trim()
  if (!name) {
    editError.value = "Name cannot be empty."
    return
  }

  const color = String(editForm.color || "").trim()
  if (color && !/^#([0-9a-fA-F]{6})$/.test(color)) {
    editError.value = "Colour must be a hex value like #RRGGBB."
    return
  }

  let geometry
  const updates = {
    name,
    description: String(editForm.description || ""),
  }

  updates.color = color || undefined

  if (isEditingCircle.value) {
    const center = parseCoordinateLine(editForm.centerText)
    if (center.error) {
      editError.value = center.error
      return
    }

    const built = buildCircleGeometry({
      centerLng: center.lng,
      centerLat: center.lat,
      radius: editForm.radius,
      radiusUnit: editForm.radiusUnit,
    })
    if (built.error) {
      editError.value = built.error
      return
    }

    const opacity = Number(editForm.opacity)
    if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) {
      editError.value = "Opacity must be between 0 and 1."
      return
    }

    geometry = built.geometry
    updates.opacity = opacity
    updates.centerLat = center.lat
    updates.centerLng = center.lng
    updates.radius = Number(editForm.radius)
    updates.radiusUnit = editForm.radiusUnit
    updates.radiusMeters = built.radiusMeters
    updates.featureType = "circle"
  } else if (isEditingSector.value) {
    const center = parseCoordinateLine(editForm.centerText)
    if (center.error) {
      editError.value = center.error
      return
    }

    const built = buildSectorGeometry({
      centerLng: center.lng,
      centerLat: center.lat,
      radius: editForm.radius,
      radiusUnit: editForm.radiusUnit,
      startBearing: editForm.startBearing,
      endBearing: editForm.endBearing,
      direction: editForm.arcDirection,
    })
    if (built.error) {
      editError.value = built.error
      return
    }

    const opacity = Number(editForm.opacity)
    if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) {
      editError.value = "Opacity must be between 0 and 1."
      return
    }

    geometry = built.geometry
    updates.opacity = opacity
    updates.centerLat = center.lat
    updates.centerLng = center.lng
    updates.radius = Number(editForm.radius)
    updates.radiusUnit = editForm.radiusUnit
    updates.radiusMeters = built.radiusMeters
    updates.startBearing = built.startBearing
    updates.endBearing = built.endBearing
    updates.arcDirection = built.direction
    updates.arcSweepDegrees = built.sweepDegrees
    updates.featureType = "sector"
  } else if (isEditingArc.value) {
    const center = parseCoordinateLine(editForm.centerText)
    if (center.error) {
      editError.value = center.error
      return
    }

    const built = buildArcGeometry({
      centerLng: center.lng,
      centerLat: center.lat,
      radius: editForm.radius,
      radiusUnit: editForm.radiusUnit,
      startBearing: editForm.startBearing,
      endBearing: editForm.endBearing,
      direction: editForm.arcDirection,
    })
    if (built.error) {
      editError.value = built.error
      return
    }

    geometry = built.geometry
    updates.centerLat = center.lat
    updates.centerLng = center.lng
    updates.radius = Number(editForm.radius)
    updates.radiusUnit = editForm.radiusUnit
    updates.radiusMeters = built.radiusMeters
    updates.startBearing = built.startBearing
    updates.endBearing = built.endBearing
    updates.arcDirection = built.direction
    updates.arcSweepDegrees = built.sweepDegrees
    updates.featureType = "arc"
  } else if (isEditingPolygon.value) {
    const parsed = parseVerticesText(editForm.verticesText)
    if (parsed.error) {
      editError.value = parsed.error
      return
    }
    geometry = parsed.geometry

    const opacity = Number(editForm.opacity)
    if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) {
      editError.value = "Opacity must be between 0 and 1."
      return
    }
    updates.opacity = opacity
  }

  if (isEditingPoint.value) {
    const icon = GOOGLE_KML_ICONS.find((i) => i.url === editForm.iconUrl) || GOOGLE_KML_ICONS[0]
    updates.iconUrl = icon.url
    updates.iconSymbol = icon.symbol
    updates.iconLabel = icon.label
  }

  emit("update-feature", { id, updates, geometry })
  cancelEdit()
}

function deleteEditing() {
  const id = editingId.value
  if (!id) return
  emit("delete-feature", { id })
  cancelEdit()
}

watch(features, () => {
  if (!editing.value || !editingId.value) return
  const exists = features.value.some((f) => featureId(f) === editingId.value)
  if (!exists) cancelEdit()
}, { deep: true, flush: "post" })
</script>

<style scoped>
.rightbar {
  border-left: 1px solid var(--color-border);
  background: var(--color-bg-raised);
  height: 100%;
  overflow: auto;
  padding: var(--space-3);
  font-family: var(--font-sans);
  color: var(--color-text);
}

.header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-2) var(--space-3) var(--space-2);
  border-bottom: 1px solid var(--color-border);
}

.title {
  font-size: var(--fs-md);
  font-weight: 600;
  color: var(--color-text);
}

.meta {
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
}

.group {
  margin: var(--space-3) 0;
}

.group-head {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--space-2);
  align-items: center;
}

.group-title {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  cursor: pointer;
  font-size: var(--fs-sm);
  color: var(--color-text);
}

.count {
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
}

.items {
  list-style: none;
  padding: var(--space-2) 0 0 0;
  margin: 0;
}

.item {
  display: block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-md);
}

.item-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-2);
  align-items: center;
}

.item.selected {
  outline: 2px solid var(--color-accent);
  outline-offset: -2px;
  background: var(--color-bg-subtle);
}
.item.duplicate {
  border: 1px solid var(--color-warn-text);
  background: var(--color-warn-bg);
}
.item.editing {
  outline: 2px solid var(--color-accent);
  outline-offset: -2px;
  background: var(--color-info-bg);
}

.item-main {
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: var(--space-2);
  align-items: center;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-md);
  color: var(--color-text);
}

.item-main:hover { background: var(--color-bg-subtle); }

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  border: 1px solid var(--color-border);
}

.poi-icon-wrap {
  width: 16px;
  height: 16px;
  display: grid;
  place-items: center;
}

.poi-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.poi-fallback {
  font-size: var(--fs-md);
  line-height: 1;
}

.item-name {
  font-size: var(--fs-sm);
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-sub { font-size: var(--fs-xs); color: var(--color-text-muted); }
.duplicate-warning {
  margin-top: var(--space-1);
  color: var(--color-warn-text);
  font-size: var(--fs-xs);
  font-weight: 600;
}
.actions { display: flex; gap: var(--space-2); }

.icon {
  border: 1px solid var(--color-border);
  background: var(--color-bg-raised);
  border-radius: var(--radius-md);
  padding: var(--space-1) var(--space-2);
  cursor: pointer;
  color: var(--color-text-muted);
}
.icon:hover { background: var(--color-bg-subtle); }

.danger {
  border: 1px solid var(--color-border);
  background: var(--color-bg-raised);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  color: var(--color-error-text);
  font-size: var(--fs-xs);
}
.danger:hover { background: var(--color-error-bg); }

.empty {
  margin-top: var(--space-4);
  font-size: var(--fs-sm);
  color: var(--color-text-muted);
  padding: var(--space-3);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
}

.editor {
  min-width: 0;
  overflow: hidden;
  margin-top: var(--space-3);
  margin-bottom: var(--space-3);
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-subtle);
}

.editor-title {
  font-size: var(--fs-md);
  font-weight: 600;
  margin: 0 0 var(--space-3);
  color: var(--color-text);
}

.field { display: grid; gap: var(--space-2); margin-bottom: var(--space-3); }
.field label { font-size: var(--fs-xs); color: var(--color-text-muted); }
.field input,
.field select {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-3);
  font-size: var(--fs-sm);
  font-family: var(--font-sans);
  background: var(--color-bg-raised);
  color: var(--color-text);
}

.color-row { display: grid; grid-template-columns: 40px 1fr; gap: var(--space-2); align-items: center; }
.color-row input[type="text"] { height: 36px; }
.color-picker {
  width: 40px;
  height: 36px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-strong);
  padding: 2px;
  cursor: pointer;
  background: var(--color-bg-raised);
}
.grid2 { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 160px); gap: var(--space-2); align-items: end; }
.wide-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.coords-input {
  width: 100%;
  min-height: 110px;
  resize: vertical;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-3);
  font-family: var(--font-mono);
  font-size: var(--fs-xs);
  background: var(--color-bg-raised);
  color: var(--color-text);
}

.row { display: flex; gap: var(--space-2); margin-top: var(--space-2); }

.primary,
.secondary {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  cursor: pointer;
  border: 1px solid var(--color-border);
  background: var(--color-bg-raised);
  color: var(--color-text);
  font-size: var(--fs-sm);
}

.primary { border-color: var(--color-border-strong); background: var(--color-bg-subtle); }
.primary:hover,
.secondary:hover { background: var(--color-bg-subtle); }

.error {
  margin: var(--space-2) 0 0;
  color: var(--color-error-text);
  background: var(--color-error-bg);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  font-size: var(--fs-sm);
  font-family: var(--font-sans);
}

button:focus-visible,
input:focus-visible,
select:focus-visible,
textarea:focus-visible {
  outline: 2px solid var(--color-focus-ring);
  outline-offset: 2px;
}

button:disabled,
button[aria-disabled="true"] {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 1100px) {
  .grid2,
  .wide-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
